/**
 * Feeds `ChatSessionNode` with a stable `setChatExpanded` and `canvasId` without closing
 * over volatile values in React Flow `nodeTypes` (which would remount every node on change).
 *
 * Expanded/collapsed state is the source of truth on each node's `data.expanded`
 * (persisted via Loro/Liveblocks in `useQuickChat`), so it survives reloads. The wrapper
 * reads it straight from `props.data` rather than from context.
 *
 * Per-user visibility for chat session nodes uses `useUserID()` inside
 * `CanvasChatSessionNodeWrapper` (not context) so the provider value stays smaller.
 *
 * Two stable RF node components are exported: interactive (main canvas) and a static
 * variant for the thumbnail capture iframe (`CanvasCapturePage`).
 */

import { useUserID } from "@/hooks/userUserID";
import type {
  CanvasChatSessionNodeData,
  CanvasChatSessionNodeProps,
} from "@/pages/canvas/types/canvasNodeData";
import { createContext, useCallback, useContext } from "react";
import { ChatSessionNode } from "../nodes/ChatSessionNode";

export type { CanvasChatSessionNodeData };

export interface CanvasChatSessionContextValue {
  /** Persists a chat session's expanded/collapsed state to its node `data`.
   *  Multiple quick chats can stay open at once — they only close on an
   *  explicit close-button click or a click on the chat session icon/node. */
  setChatExpanded: (id: string, expanded: boolean) => void;
  canvasId: string;
}

export const canvasChatSessionContextDefault: CanvasChatSessionContextValue = {
  setChatExpanded: () => {},
  canvasId: "",
};

const CanvasChatSessionContext = createContext<CanvasChatSessionContextValue>(
  canvasChatSessionContextDefault,
);

export const CanvasChatSessionProvider = CanvasChatSessionContext.Provider;

/** Module-level stable ref for `nodeTypes.chatSession` on the interactive canvas. */
export function CanvasChatSessionNodeWrapper(
  props: CanvasChatSessionNodeProps,
) {
  const { setChatExpanded, canvasId } = useContext(CanvasChatSessionContext);
  const currentUserId = useUserID();

  const onToggleExpanded = useCallback(
    (expanded: boolean) => {
      setChatExpanded(props.id, expanded);
    },
    [props.id, setChatExpanded],
  );

  if (props.data?.userId && props.data.userId !== currentUserId) return null;

  return (
    <ChatSessionNode
      {...props}
      isExpanded={!!props.data?.expanded}
      onToggleExpanded={onToggleExpanded}
      canvasId={canvasId}
    />
  );
}

/** Module-level stable ref for `nodeTypes.chatSession` in the capture iframe (non-interactive). */
export function CanvasChatSessionNodeCaptureWrapper(
  props: CanvasChatSessionNodeProps,
) {
  const { canvasId } = useContext(CanvasChatSessionContext);
  return (
    <ChatSessionNode
      {...props}
      isExpanded={false}
      onToggleExpanded={() => {}}
      canvasId={canvasId}
    />
  );
}
